import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin } from "@phosphor-icons/react";
import PageHero from "../components/PageHero";
import { Section, SectionHeading, FadeIn } from "../components/Section";
import Button from "../components/Button";
import { INDUSTRIES } from "../data/content";

const Industries = () => {
  return (
    <>
      <PageHero
        overline="Industries"
        title="Sector-specific energy solutions."
        description="From oil & gas operators to manufacturers, marine fleets and public infrastructure — we deliver fit-for-purpose solutions tuned to each industry's operational reality."
        image="https://images.pexels.com/photos/29056690/pexels-photo-29056690.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
        testid="industries-hero"
      />

      <Section spacing="py-20 md:py-28">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {INDUSTRIES.map((ind, i) => (
            <motion.div
              key={ind.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="group relative overflow-hidden border border-border bg-card hover:border-primary transition-all hover:-translate-y-1"
              data-testid={`industry-card-${i}`}
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={ind.image}
                  alt={ind.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/30 to-transparent" />
                <div className="absolute top-4 left-4 font-mono text-xs text-primary bg-background/80 px-2 py-1">
                  0{i + 1}
                </div>
              </div>
              <div className="p-7">
                <h3 className="font-display font-bold text-xl mb-3 group-hover:text-primary transition-colors">
                  {ind.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {ind.description}
                </p>
                <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-foreground group-hover:text-primary transition-colors">
                  Discuss
                  <ArrowUpRight
                    size={14}
                    weight="bold"
                    className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Landmark Project */}
      <Section className="bg-card border-y border-border" spacing="py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <FadeIn>
              <div className="overline text-primary mb-4">— Landmark project</div>
              <h2 className="font-display font-bold tracking-tight text-3xl md:text-5xl leading-[1.05]">
                The Ogan/Agbor — Awka pipeline.
              </h2>
              <p className="mt-5 text-muted-foreground leading-relaxed">
                A flagship 18″ × 153 km natural-gas transmission corridor —
                connecting Delta State to Anambra via Asaba, Onitsha and Nnewi
                — paired with a state-of-the-art Compressed Natural Gas (CNG)
                facility. The programme strengthens Nigeria's gas backbone,
                fuels industry across the South-East and showcases our
                capability to execute complex linear infrastructure end-to-end.
              </p>
              <Button to="/contact" className="mt-8" iconRight={ArrowUpRight}>
                Engage our team
              </Button>
            </FadeIn>
          </div>
          <div className="lg:col-span-7">
            <FadeIn delay={0.1}>
              <div className="grid grid-cols-2 gap-4">
                {[
                  {
                    label: "Diameter",
                    value: "18 inches",
                  },
                  {
                    label: "Length",
                    value: "153 km",
                  },
                  {
                    label: "Corridor",
                    value: "Delta → Anambra",
                  },
                  {
                    label: "Includes",
                    value: "CNG facility",
                  },
                ].map((m) => (
                  <div key={m.label} className="p-6 border border-border">
                    <div className="overline text-muted-foreground mb-2">
                      {m.label}
                    </div>
                    <div className="font-display font-bold text-2xl tracking-tight">
                      {m.value}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-6 border border-border flex items-start gap-3">
                <MapPin size={20} className="text-primary mt-0.5" />
                <div>
                  <div className="overline text-muted-foreground mb-1">
                    Route
                  </div>
                  <div className="text-sm leading-relaxed">
                    Ogan/Agbor (Delta) → Asaba → Onitsha → Nnewi → Awka
                    (Anambra)
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </Section>
    </>
  );
};

export default Industries;
